import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { REGEXP_ONLY_DIGITS } from "input-otp";
import { ArrowLeft, MailCheck, RefreshCw, ShieldCheck } from "lucide-react";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { useBotGuard } from "@/components/BotTrap";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const OTP_LENGTH = 6;

export default function VerifyEmail() {
  const { verifyOtp, resendOtp } = useAuth();
  const { trap, guard } = useBotGuard();
  const nav = useNavigate();
  const location = useLocation();

  const initialEmail = useMemo(() => {
    const fromState = location.state?.email || "";
    const fromQuery = new URLSearchParams(location.search).get("email") || "";
    return (fromState || fromQuery).trim().toLowerCase();
  }, [location.state, location.search]);

  const [email, setEmail] = useState(initialEmail);
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState(false);
  const [err, setErr] = useState("");
  const [info, setInfo] = useState(
    location.state?.justRegistered
      ? `We sent a 6-digit verification code to ${initialEmail || "your email"}.`
      : "",
  );
  const [cooldown, setCooldown] = useState(() => {
    const retry = Number(location.state?.retryAfter);
    return Number.isFinite(retry) && retry > 0 ? retry : initialEmail ? 60 : 0;
  });

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timer = setTimeout(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const submit = async (codeOverride) => {
    const code = String(codeOverride ?? otp).trim();
    const address = email.trim().toLowerCase();
    setErr("");
    if (!address) {
      setErr("Enter the email address you signed up with.");
      return;
    }
    if (code.length !== OTP_LENGTH) {
      setErr("Enter the 6-digit code from your email.");
      return;
    }
    if (busy) return;

    setBusy(true);
    try {
      await verifyOtp(address, code, guard(false));
      toast.success("Email verified — welcome aboard");
      nav("/app", { replace: true });
    } catch (e) {
      setErr(formatApiError(e));
      setOtp("");
    } finally {
      setBusy(false);
    }
  };

  const onResend = async () => {
    const address = email.trim().toLowerCase();
    setErr("");
    if (!address) {
      setErr("Enter the email address you signed up with.");
      return;
    }
    setResending(true);
    try {
      const data = await resendOtp(address, guard(false));
      setOtp("");
      setInfo(data?.message || "If that email is registered, a new code is on its way.");
      const retry = Number(data?.retry_after);
      setCooldown(Number.isFinite(retry) && retry > 0 ? retry : 60);
      toast.success("Verification code requested");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && Number(detail.retry_after) > 0) {
        setCooldown(Number(detail.retry_after));
      }
      setErr(formatApiError(e));
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--bg)] grid md:grid-cols-2">
      <div className="hidden md:flex flex-col justify-between p-10 border-r border-[var(--border)] bg-white">
        <Link to="/" className="flex items-center gap-2" data-testid="link-brand">
          <div className="w-8 h-8 rounded-md bg-[var(--fg)] text-white grid place-items-center font-bold">
            IF
          </div>
          <span className="font-semibold tracking-tight">InvoiceForge</span>
        </Link>

        <div>
          <div className="overline mb-4">Email verification</div>
          <h1 className="text-5xl leading-none tracking-tight font-medium">
            Check your <br />
            <span className="font-serif italic">inbox</span>.
          </h1>
          <p className="mt-6 text-gray-600 max-w-md">
            Every new account is confirmed by email before the workspace opens.
            Enter the 6-digit code and your dashboard unlocks instantly.
          </p>

          <div className="mt-10 space-y-4 max-w-md">
            {[
              { icon: MailCheck, label: "Look for “Prismgrim Invoice Forge”", copy: "The code arrives from our transactional mail service." },
              { icon: ShieldCheck, label: "One code, one account", copy: "Codes expire in 10 minutes and can’t be reused." },
              { icon: RefreshCw, label: "Didn’t arrive?", copy: "Request a fresh code — earlier codes stop working." },
            ].map(({ icon: Icon, label, copy }) => (
              <div key={label} className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-md border border-[var(--border)] bg-[var(--bg)] grid place-items-center shrink-0">
                  <Icon className="w-4 h-4 text-[var(--primary)]" />
                </div>
                <div>
                  <div className="text-sm font-medium">{label}</div>
                  <div className="text-xs text-gray-500 leading-relaxed mt-0.5">{copy}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="overline">Prismgrim Invoice Forge · Secure signup</div>
      </div>

      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="overline mb-3">One last step</div>
          <h2 className="text-3xl font-medium tracking-tight mb-3">Verify your email.</h2>
          <p className="text-sm text-gray-600 mb-8 leading-relaxed">
            We sent a verification code to{" "}
            <span className="font-medium text-[var(--fg)] break-all">
              {email || "your email address"}
            </span>
            . Enter it below to open your workspace.
          </p>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              submit();
            }}
          >
            {trap}
            <label className="block mb-4">
              <div className="text-xs text-gray-600 mb-1.5">Email</div>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setErr("");
                }}
                readOnly={Boolean(initialEmail)}
                className={`w-full h-11 px-3 border border-[var(--border)] rounded-md outline-none focus:border-[var(--primary)] focus:ring-2 focus:ring-[var(--primary)]/20 ${
                  initialEmail ? "bg-gray-50 text-gray-600" : "bg-white"
                }`}
                data-testid="verify-email-input"
              />
              {initialEmail && (
                <div className="mt-1.5 text-[11px] text-gray-500">
                  Wrong address?{" "}
                  <Link
                    to="/login"
                    className="underline underline-offset-2 hover:text-[var(--fg)]"
                    data-testid="verify-use-other-email"
                  >
                    Sign in with a different account
                  </Link>
                </div>
              )}
            </label>

            <div className="block mb-6">
              <div className="text-xs text-gray-600 mb-2">Verification code</div>
              <div className="flex items-center gap-3 flex-wrap">
                <InputCode
                  value={otp}
                  onChange={(value) => {
                    setOtp(value);
                    setErr("");
                  }}
                  onComplete={submit}
                  disabled={busy}
                />
                <div className="text-[11px] text-gray-500 font-mono uppercase tracking-wider">
                  {OTP_LENGTH} digits
                </div>
              </div>
            </div>

            {err && (
              <div
                className="mb-4 text-sm text-red-600"
                data-testid="verify-email-error"
              >
                {err}
              </div>
            )}
            {info && !err && (
              <div
                className="mb-4 text-xs text-gray-600 rounded-md border border-[var(--border)] bg-white px-3 py-2.5 leading-relaxed"
                data-testid="verify-email-info"
              >
                {info}
              </div>
            )}

            <button
              type="submit"
              className="w-full btn-primary disabled:opacity-50"
              disabled={busy || otp.length !== OTP_LENGTH || !email}
              data-testid="verify-email-submit"
            >
              {busy ? "Verifying…" : "Verify & open dashboard"}
            </button>
          </form>

          <div className="mt-5 flex items-center justify-between gap-3 text-sm">
            <button
              type="button"
              onClick={onResend}
              disabled={resending || cooldown > 0 || !email}
              className="inline-flex items-center gap-2 text-[var(--fg)] disabled:text-gray-400 disabled:cursor-not-allowed hover:underline underline-offset-4"
              data-testid="verify-email-resend"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${resending ? "animate-spin" : ""}`} />
              {cooldown > 0 ? `Resend in ${cooldown}s` : resending ? "Sending…" : "Resend code"}
            </button>

            <Link
              to="/login"
              className="inline-flex items-center gap-1.5 text-gray-500 hover:text-[var(--fg)]"
              data-testid="verify-email-back"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Back to sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function InputCode({ value, onChange, onComplete, disabled }) {
  return (
    <InputOTP
      maxLength={OTP_LENGTH}
      value={value}
      onChange={onChange}
      onComplete={onComplete}
      pattern={REGEXP_ONLY_DIGITS}
      disabled={disabled}
      containerClassName="justify-start"
      data-testid="verify-email-otp"
    >
      <InputOTPGroup className="gap-2">
        {Array.from({ length: OTP_LENGTH }).map((_, index) => (
          <InputOTPSlot
            key={index}
            index={index}
            className="h-12 w-11 rounded-md border border-[var(--border)] bg-white text-lg font-mono shadow-none first:rounded-md last:rounded-md"
          />
        ))}
      </InputOTPGroup>
    </InputOTP>
  );
}
